<script setup lang="ts">
import type { IInsuranceCompanyCard } from '~/components/entity/insurance/types'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { Rating } from '~/components/ui/rating'
import { UiCardTop } from '~/components/ui/card'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

const props = withDefaults(
  defineProps<{
    item: IInsuranceCompanyCard
    hideOrganizationDetails?: boolean
    position?: IEntityPosition
  }>(),
  {
    hideOrganizationDetails: false,
    position: undefined
  }
)

const isHighlighted = useEntityHighlight(props.item, props.position)

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <EntityOrganizationLogo
          v-if="!props.hideOrganizationDetails"
          :src="props.item.logo"
          :alt="plainTitle"
          :to="props.item.url"
          :aria-label="$t('entity.insurance.item.more_about_company', { title: plainTitle })"
          :highlighted="isHighlighted"
        />
        <div>
          <NuxtLinkLocale
            :to="props.item.url"
            :aria-label="
              $t('entity.insurance.item.more_about_company', { title: plainTitle })
            "
            class="inline-block hover:underline"
          >
            <Heading tag="h6" class="hit-title"><span v-hydrated-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <p v-if="props.item.official_title" class="text-gray-700">
            {{ props.item.official_title }}
          </p>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="props.item.ratings" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div class="flex justify-between gap-x-2 sm:grid sm:grid-cols-2 sm:gap-y-2">
        <div v-if="props.item.email">
          <div class="SecondaryTextHeadings">{{ $t('common.email') }}</div>
          <a
            v-hydrated-html="props.item.email.replace('@', '@<wbr>')"
            :href="`mailto:${props.item.email}`"
            class="font-semibold text-blue-600 hover:underline"
          />
        </div>
        <div v-if="props.item.website_url">
          <div class="SecondaryTextHeadings">{{ $t('common.website') }}</div>
          <a
            :href="props.item.website_url"
            class="font-semibold text-blue-600 hover:underline"
            target="_blank"
            rel="noopener"
          >
            {{ $t('common.go_to_website') }}
          </a>
        </div>
      </div>
    </div>
  </article>
</template>
